/**
 * Base Field Renderers
 * 
 * Common field renderers for boolean, multiValued, number, text, and HTML fields.
 * These are the default renderers used when no entity-type-specific renderer applies.
 */

import React from 'react';
import {
    TextInput,
    Textarea,
    Checkbox,
    Box,
    Text,
    Paper
} from '@mantine/core';
import { ScimAceEditor } from '../../common/ScimAceEditor';
import { stringifyDebugNotes } from '../../../utils/debugLog';
import { FieldLabelWithRequired } from '../renderDynamicField';
import { FieldRendererProps, getFieldName, getFieldLabel, isHtmlField } from './types';
import { createEntityRef, isA } from '../../../types';

/**
 * Render a boolean field as a checkbox
 */
export const BooleanFieldRenderer: React.FC<FieldRendererProps> = ({
    attr,
    value,
    loading,
    handleFieldChange,
    entityType
}) => {
    const name = getFieldName(attr);
    const label = getFieldLabel(attr);
    const isRequired = attr.required || false;

    const debugNotes = stringifyDebugNotes({
        name,
        label,
        type: attr.type,
        value,
        isRequired,
        computed: attr.computed,
        mutability: attr.mutability,
        entityType
    });

    return (
        <Checkbox
            key={name}
            checked={value || false}
            onChange={(e) => handleFieldChange(name, e.currentTarget.checked)}
            disabled={loading}
            label={label}
            required={isRequired}
            mb="md"
            data-notes={debugNotes}
            data-field={name}
            data-renderer="Checkbox"
            data-type="boolean"
        />
    );
};

/**
 * Render a multi-valued field as comma-separated text input
 */
export const MultiValuedFieldRenderer: React.FC<FieldRendererProps> = ({
    attr,
    value,
    loading,
    handleFieldChange
}) => {
    const name = getFieldName(attr);
    const label = getFieldLabel(attr);
    const isRequired = attr.required || false;

    const displayValue = Array.isArray(value) ? value.join(', ') : (value || '');
    return (
        <TextInput
            key={name}
            label={label}
            value={displayValue}
            onChange={(e) => {
                const inputValue = e.target.value;
                const arrayValue = inputValue.split(',').map(v => v.trim()).filter(v => v);
                handleFieldChange(name, arrayValue);
            }}
            onBlur={(e) => {
                const trimmed = e.currentTarget.value.trim();
                const arrayValue = trimmed ? trimmed.split(',').map(v => v.trim()).filter(Boolean) : [];
                const normalizedDisplay = arrayValue.join(', ');
                if (normalizedDisplay !== displayValue) handleFieldChange(name, arrayValue);
            }}
            disabled={loading}
            required={isRequired}
            description={isRequired ? 'Required' : 'Comma-separated values'}
            mb="md"
        />
    );
};

/**
 * Render a number/integer field
 */
export const NumberFieldRenderer: React.FC<FieldRendererProps> = ({
    attr,
    value,
    loading,
    handleFieldChange
}) => {
    const name = getFieldName(attr);
    const label = getFieldLabel(attr);
    const isRequired = attr.required || false;

    const displayValue = value ?? '';
    return (
        <TextInput
            key={name}
            label={label}
            type="number"
            value={displayValue}
            onChange={(e) => handleFieldChange(name, e.target.value ? Number(e.target.value) : '')}
            onBlur={(e) => {
                const trimmed = e.currentTarget.value.trim();
                if (trimmed !== String(displayValue)) {
                    handleFieldChange(name, trimmed === '' ? '' : (Number(trimmed) || ''));
                }
            }}
            disabled={loading}
            required={isRequired}
            mb="md"
        />
    );
};

/**
 * Render an HTML editor field (for body/html content)
 */
export const HtmlFieldRenderer: React.FC<FieldRendererProps> = ({
    attr,
    value,
    loading,
    handleFieldChange
}) => {
    const name = getFieldName(attr);
    const label = getFieldLabel(attr);
    const isRequired = attr.required || false;

    return (
        <Box key={name} mb="md">
            <Text size="sm" fw={500} mb="xs">
                <FieldLabelWithRequired label={label} required={isRequired} readOnly={loading} />
            </Text>
            <Paper withBorder p="xs">
                <ScimAceEditor
                    mode="html"
                    value={typeof value === 'string' ? value : ''}
                    onChange={(newValue) => handleFieldChange(name, newValue)}
                    name={`html-editor-${name}`}
                    height="400px"
                    fontSize={14}
                    showPrintMargin={false}
                    readOnly={loading}
                />
            </Paper>
        </Box>
    );
};

/**
 * Render a default text field
 */
export const TextFieldRenderer: React.FC<FieldRendererProps> = ({
    attr,
    value,
    loading,
    handleFieldChange
}) => {
    const name = getFieldName(attr);
    const label = getFieldLabel(attr);
    const isRequired = attr.required || false;
    
    // Determine if this should be a multiline field
    const nameRef = createEntityRef(name);
    const isMultiline = attr.type === 'text' &&
        (isA(nameRef, 'Description') || isA(nameRef, 'Script') ||
            label.toLowerCase().includes('description') || label.toLowerCase().includes('script'));

    const displayValue = value || '';
    const handleBlurTrim = (e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const trimmed = e.currentTarget.value.trim();
        if (trimmed !== displayValue) handleFieldChange(name, trimmed);
    };
    return isMultiline ? (
        <Textarea
            key={name}
            label={label}
            value={displayValue}
            onChange={(e) => handleFieldChange(name, e.target.value)}
            onBlur={handleBlurTrim}
            disabled={loading}
            required={isRequired}
            rows={3}
            mb="md"
        />
    ) : (
        <TextInput
            key={name}
            label={label}
            value={displayValue}
            onChange={(e) => handleFieldChange(name, e.target.value)}
            onBlur={handleBlurTrim}
            disabled={loading}
            required={isRequired}
            mb="md"
        />
    );
};

/**
 * Render a field based on its type (dispatcher function)
 */
export const renderBaseField = (props: FieldRendererProps): React.ReactNode => {
    const { attr, value } = props;
    const name = getFieldName(attr);
    const label = getFieldLabel(attr);

    // Boolean field
    if (attr.type === 'boolean') {
        return <BooleanFieldRenderer key={name} {...props} />;
    }

    // Multi-valued field
    if (attr.multiValued) {
        return <MultiValuedFieldRenderer key={name} {...props} />;
    }

    // Number/integer field
    if (attr.type === 'number' || attr.type === 'integer') {
        return <NumberFieldRenderer key={name} {...props} />;
    }

    // HTML editor for body/html fields
    if (isHtmlField(name, label)) {
        return <HtmlFieldRenderer key={name} {...props} />;
    }

    // Default to text field
    return <TextFieldRenderer key={name} {...props} />;
};
